import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from './useLanguage';

/**
 * WCAG 2.1 2.4.2 Page Titled (Level A).
 *
 * The criterion asks that every "web page" have a title describing its topic.
 * In a single-page app each route IS a page for this purpose, so the static
 * <title> in index.html satisfies it only on the landing route — every other
 * route inherited a title describing something else. That also degrades browser
 * history and tab lists, where the title is the only label a user gets.
 *
 * Titles are composed from the existing nav strings rather than new ones, so a
 * route's title and the link that leads to it can never drift apart, and both
 * languages are already covered.
 */

// Matched against the path with its /:lang prefix removed. Longest match wins,
// so 'events/:id' picks up the 'events' title rather than falling through.
const TITLE_KEYS: ReadonlyArray<readonly [prefix: string, translationKey: string]> = [
  ['offerings', 'header.nav.donate'],
  ['events', 'header.nav.events'],
  ['calendar', 'header.nav.events'],
  ['admin', 'header.nav.admin'],
  ['privacy', 'footer.privacyPolicy'],
  ['terms', 'footer.termsOfConditions'],
];

export const routeTitle = (
  pathname: string,
  t: (key: string) => string
): string => {
  const siteName = t('header.title');
  // Drop the leading /en or /si; everything after it identifies the route.
  const route = pathname.replace(/^\/[a-z]{2}(?=\/|$)/, '').replace(/^\//, '');

  if (!route) return siteName;

  const match = TITLE_KEYS.find(([prefix]) => route === prefix || route.startsWith(`${prefix}/`));
  if (!match) return siteName;

  const section = t(match[1]);
  // A missing translation returns the key itself in this app; falling back to
  // the bare site name beats putting "header.nav.events" in the title bar.
  return section && !section.includes('.') ? `${section} · ${siteName}` : siteName;
};

export const usePageTitle = (): void => {
  const { pathname } = useLocation();
  const { t, loading } = useLanguage();

  useEffect(() => {
    // Translations load asynchronously; setting a title mid-load would write
    // raw keys into the tab and then correct itself, which screen readers
    // announce twice.
    if (loading) return;
    document.title = routeTitle(pathname, t);
  }, [pathname, t, loading]);
};
